import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../../config/firebase';

export const uploadImage = async (file, path) => {
  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { url: downloadURL, error: null };
  } catch (error) {
    return { url: null, error: error.message };
  }
};

export const uploadAudio = async (audioBlob, bookId) => {
  try {
    const uid = auth?.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    
    const timestamp = Date.now();
    const path = `users/${uid}/audio-recordings/${bookId}/${timestamp}.mp4`;
    
    const metadata = {
      contentType: 'audio/mp4',
    };
    
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, audioBlob, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { url: downloadURL, path, error: null };
  } catch (error) {
    console.error('Error uploading audio:', error);
    return { url: null, path: null, error: error.message };
  }
};

export const deleteAudio = async (storagePath) => {
  try {
    if (!storagePath) throw new Error('Storage path is required');
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting audio:', error);
    return { success: false, error: error.message };
  }
};

export const uploadPDF = async (pdfBlob, bookId, pdfType = 'verticalContent') => {
  try {
    // Validate PDF type
    const validTypes = ['cover', 'verticalContent', 'horizontalContent'];
    if (!validTypes.includes(pdfType)) {
      throw new Error(`Invalid PDF type: ${pdfType}. Must be one of: ${validTypes.join(', ')}`);
    }
    
    // Require auth and create user-scoped path
    const uid = auth?.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');
    const fileName = `${pdfType}.pdf`;
    const path = `users/${uid}/books/${bookId}/${fileName}`;
    
    // Set metadata to force download when the file is accessed
    const metadata = {
      contentType: 'application/pdf',
      customMetadata: {
        'Content-Disposition': `attachment; filename="${pdfType}.pdf"`
      }
    };
    
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, pdfBlob, metadata);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return { url: downloadURL, path, error: null };
  } catch (error) {
    console.error('Error uploading PDF:', error);
    return { url: null, path: null, error: error.message };
  }
};

export const uploadWorldObjectImage = async (file, bookId) => {
  try {
    console.log('Starting world object image upload:', { fileName: file.name, fileSize: file.size, fileType: file.type, bookId });
    
    // Validate file is an image
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image (PNG, JPG, GIF, WebP, etc.)');
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      throw new Error('File size must be less than 10MB');
    }

    const uid = auth?.currentUser?.uid;
    if (!uid) throw new Error('Not authenticated');

    const timestamp = Date.now();
    const fileName = `${timestamp}-${file.name}`;
    const path = `users/${uid}/books/${bookId}/world-objects/${fileName}`;

    console.log('Upload path:', path);

    const metadata = {
      contentType: file.type,
    };

    const storageRef = ref(storage, path);
    console.log('Starting uploadBytes...');
    const snapshot = await uploadBytes(storageRef, file, metadata);
    console.log('Upload complete, getting download URL...');
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Download URL obtained:', downloadURL);
    return { url: downloadURL, path, error: null };
  } catch (error) {
    console.error('Error uploading world object image:', error);
    return { url: null, path: null, error: error.message };
  }
};